import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { filter as applyFilter } from "@common/filter";
import { fromDayMarker, parseBackendIso } from "../utils";
import { Props } from "./types";
import { Event } from "./event";
import { Milestone } from "./milestone";
import { Unavailability } from "./unavailability";
import { Holiday } from "./holiday";
import { Task } from "./task";
import { DayCreationMenu } from "./dayCreationMenu";
import styles from "./styles.scss";

const EVENT_HEIGHT = 30;

function assignRows(items: { from: number; to: number }[]): number[] {
  const rows: number[] = [];

  for (let i = 0; i < items.length; i++) {
    const occupiedRows = new Set<number>();

    for (let j = 0; j < i; j++) {
      if (items[j].from <= items[i].to && items[j].to >= items[i].from) {
        occupiedRows.add(rows[j]);
      }
    }

    let row = 0;
    while (occupiedRows.has(row)) row++;
    rows.push(row);
  }

  return rows;
}

function Week({
  selectedDate,
  week,
  events,
  unavailabilities,
  milestones,
  calendarsHolidays,
  tasks,
  filter,
}: Props) {
  const { t } = useTranslation();
  const userTimezone = DateTime.local().zoneName;
  const today = DateTime.now().setZone(userTimezone);
  const [isExtended, setIsOpen] = React.useState(false);

  const [creationMenu, setCreationMenu] = React.useState<{
    day: Date;
    x: number;
    y: number;
  } | null>(null);

  const filteredHolidays = applyFilter(
    calendarsHolidays,
    filter,
    (calendarHolidays) => ({ calendarHolidays }),
  );
  const filteredUnavailabilities = applyFilter(
    unavailabilities,
    filter,
    (unavailability) => ({ unavailability }),
  );
  const filteredEvents = applyFilter(
    events,
    filter,
    ({ project, ...event }) => ({ project, event }),
  );
  const filteredMilestones = applyFilter(
    milestones,
    filter,
    ({ project, ...milestone }) => ({ project, milestone }),
  );
  const filteredTasks = applyFilter(tasks, filter, ({ project, ...task }) => ({
    project,
    task,
  }));

  const maxCollapsed = 3;

  const allItems: {
    type: string;
    from: number;
    to: number;
    key: string;
    data: any;
  }[] = [];

  filteredHolidays.forEach((ch) => {
    ch.holidays.forEach((holiday, hi) => {
      const wd = parseBackendIso(holiday.holidayDate).weekday;
      const col = wd === 7 ? 7 : wd;
      allItems.push({
        type: "holiday",
        from: col,
        to: col,
        key: `holiday-${ch.uuid}-${hi}`,
        data: { calendarHolidays: ch, holiday },
      });
    });
  });

  filteredUnavailabilities.forEach((u) => {
    allItems.push({
      type: "unavailability",
      from: u.from,
      to: u.to,
      key: `unavailability-${u.uuid}`,
      data: u,
    });
  });

  filteredEvents.forEach((e) => {
    allItems.push({
      type: "event",
      from: e.from,
      to: e.to,
      key: `event-${e.uuid}`,
      data: e,
    });
  });

  filteredMilestones.forEach((m) => {
    allItems.push({
      type: "milestone",
      from: m.from,
      to: m.to,
      key: `milestone-${m.uuid}`,
      data: m,
    });
  });

  filteredTasks.forEach((task) => {
    allItems.push({
      type: "task",
      from: task.from,
      to: task.to,
      key: `task-${task.uuid}`,
      data: task,
    });
  });

  const rows = assignRows(allItems);
  const totalRows = rows.length > 0 ? Math.max(...rows) + 1 : 0;

  function getItemCountAtDay(dayCol: number) {
    return allItems.filter((item) => item.from <= dayCol && item.to >= dayCol)
      .length;
  }

  const weekStart = fromDayMarker(week[0]);
  const weekEnd = fromDayMarker(week[week.length - 1]);

  return (
    <div className={styles.week}>
      <ul className={styles.grid}>
        {week.map((day, index) => {
          const dayCol = index + 1;
          const dayItemCount = getItemCountAtDay(dayCol);
          const dayDt = fromDayMarker(day);
          const selectedDt = selectedDate ? fromDayMarker(selectedDate) : null;
          const isToday =
            dayDt.year === today.year &&
            dayDt.month === today.month &&
            dayDt.day === today.day;
          const isWeekend = dayDt.weekday === 6 || dayDt.weekday === 7;
          const isSelected =
            !!selectedDt &&
            selectedDt.year === dayDt.year &&
            selectedDt.month === dayDt.month &&
            selectedDt.day === dayDt.day;

          return (
            <li
              key={`${day}__${index}`}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setCreationMenu({ day, x: e.clientX, y: e.clientY });
              }}
              className={classNames(styles.day, {
                [styles.today]: isToday,
                [styles.empty]: dayItemCount === 0,
                [styles.weekEndDay]: isWeekend,
                [styles.selectedDate]: isSelected,
              })}
              style={{
                height: isExtended ? (totalRows + 2) * EVENT_HEIGHT : undefined,
              }}
            >
              <p className={styles.dayIndex}>{dayDt.day}</p>
              {dayItemCount > maxCollapsed && (
                <div
                  className={styles.more}
                  style={{ height: EVENT_HEIGHT }}
                  onClick={() => setIsOpen(!isExtended)}
                >
                  {!isExtended ? (
                    <p>{t("calendarPage.showMoreEvents")}</p>
                  ) : (
                    <p>{t("calendarPage.showLessEvents")}</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {allItems
        .map((item, i) => ({ ...item, row: rows[i] }))
        .filter((item) => isExtended || item.row < maxCollapsed)
        .map((item) => (
          <div
            key={item.key}
            className={styles.event}
            style={{
              height: EVENT_HEIGHT,
              top: (item.row + 1) * EVENT_HEIGHT,
              left: `${(100 / 7) * (item.from - 1)}%`,
              width: `${(100 / 7) * (item.to - item.from + 1)}%`,
            }}
          >
            <div className={styles.wrapper}>
              {item.type === "holiday" && (
                <Holiday
                  isDiscontinuedLeft={false}
                  isDiscontinuedRight={false}
                  calendarHolidays={item.data.calendarHolidays}
                  holiday={item.data.holiday}
                />
              )}
              {item.type === "unavailability" && (
                <Unavailability
                  unavailability={item.data}
                  isDiscontinuedLeft={
                    parseBackendIso(item.data.startDate) < weekStart
                  }
                  isDiscontinuedRight={
                    parseBackendIso(item.data.endDate || item.data.startDate) >
                    weekEnd
                  }
                />
              )}
              {item.type === "event" && (
                <Event
                  event={item.data}
                  isDiscontinuedLeft={
                    parseBackendIso(item.data.startTime) < weekStart
                  }
                  isDiscontinuedRight={
                    parseBackendIso(item.data.endTime) > weekEnd
                  }
                />
              )}
              {item.type === "milestone" && <Milestone milestone={item.data} />}
              {item.type === "task" && (
                <Task
                  task={item.data}
                  isDiscontinuedLeft={
                    !!item.data.startDate &&
                    parseBackendIso(item.data.startDate) < weekStart
                  }
                  isDiscontinuedRight={
                    !!item.data.endDate &&
                    parseBackendIso(item.data.endDate) > weekEnd
                  }
                />
              )}
            </div>
          </div>
        ))}
      {creationMenu && (
        <DayCreationMenu
          day={creationMenu.day}
          x={creationMenu.x}
          y={creationMenu.y}
          openedLeft={false}
          onClose={() => setCreationMenu(null)}
        />
      )}
    </div>
  );
}

export { Week };
